/**
 * Onboarding Progress
 *
 * Step 2: Shows live pipeline progress while ad is being generated.
 */

import { AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { useOnboarding } from '../../contexts/OnboardingContext'
import PhaseProgressBar from '../ad-generator/components/PhaseProgressBar'

export default function OnboardingProgress() {
  const { state, retry } = useOnboarding()
  const { currentJob, error } = state

  const isRunning = !error && (currentJob?.status === 'running' || currentJob?.status === 'pending')
  const isFailed = !!error || currentJob?.status === 'failed' || currentJob?.status === 'cancelled'

  // Get current stage insight
  const currentStageData = currentJob?.stages?.find(s => s.name === currentJob.current_stage)
  const insight = currentStageData?.insight

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6',
            isFailed
              ? 'bg-red-500/10'
              : 'bg-gradient-to-br from-blue-500/20 to-purple-600/20'
          )}>
            {isFailed ? (
              <AlertCircle className="w-10 h-10 text-red-500" />
            ) : (
              <div className="w-12 h-12 border-3 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-tf-text mb-3">
            {isFailed ? 'Something Went Wrong' : 'Creating Your Ad'}
          </h1>
          <p className="text-tf-text-muted text-lg">
            {isFailed
              ? (error || currentJob?.error_message || 'An error occurred during generation')
              : "We're analyzing your brand and crafting a perfect ad"}
          </p>
        </div>

        {/* Progress Card */}
        <div className="bg-tf-bg-card rounded-2xl border border-tf-border p-8 shadow-sm">
          {currentJob && !isFailed && (
            <PhaseProgressBar
              stages={currentJob.stages}
              currentStage={currentJob.current_stage}
              jobStatus={currentJob.status}
            />
          )}

          {/* Current insight */}
          {isRunning && insight && (
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-500">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-sm font-medium">{insight}</span>
              </div>
            </div>
          )}

          {/* Progress percentage */}
          {isRunning && currentJob && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-tf-text-muted">Progress</span>
                <span className="text-sm font-medium text-tf-text">{currentJob.progress_pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-tf-bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-500"
                  style={{ width: `${currentJob.progress_pct}%` }}
                />
              </div>
            </div>
          )}

          {/* Retry button for failures */}
          {isFailed && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={retry}
                className={cn(
                  'flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all',
                  'bg-tf-bg-muted text-tf-text hover:bg-tf-bg-hover'
                )}
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Helpful tips while waiting + retry option */}
        {isRunning && (
          <div className="mt-8 text-center space-y-4">
            <p className="text-sm text-tf-text-muted">
              This usually takes 2-3 minutes. We're working hard to create the perfect ad for you.
            </p>
            {currentJob && currentJob.progress_pct === 0 && (
              <button
                onClick={retry}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-tf-text-muted hover:text-tf-text bg-tf-bg-muted/50 hover:bg-tf-bg-muted transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Not progressing? Start over
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
